import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { X, ZoomIn } from 'lucide-react';

const galleryItems = [
  { id: 1, category: 'expo', title: 'India Retail Investors Expo 2024', image: 'https://images.unsplash.com/photo-1768396855390-0728fa9c21e1?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 2, category: 'stall', title: 'Nexus Exhibition Booth', image: 'https://images.unsplash.com/photo-1761195689615-9469b65dac01?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 3, category: 'event', title: 'The FinRun Marathon 2024', image: 'https://images.unsplash.com/photo-1746046489457-9628dc3b8a1f?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 4, category: 'activation', title: 'Darwinbox Brand Activation', image: 'https://images.pexels.com/photos/29708240/pexels-photo-29708240.jpeg' },
  { id: 5, category: 'expo', title: 'Tech Innovation Summit', image: 'https://images.unsplash.com/photo-1740727263760-d220d5a90d9f?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 6, category: 'stall', title: 'Bajaj Electronics Booth', image: 'https://images.pexels.com/photos/860227/pexels-photo-860227.jpeg' },
  { id: 7, category: 'event', title: 'Corporate Awards Night', image: 'https://images.unsplash.com/photo-1560439514-07abbb294a86?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 8, category: 'activation', title: 'Product Launch Event', image: 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=jpg&q=85' },
  { id: 9, category: 'expo', title: 'Finance & Investment Expo', image: 'https://images.pexels.com/photos/7567438/pexels-photo-7567438.jpeg' },
  { id: 10, category: 'event', title: 'Marathon Finish Line', image: 'https://images.pexels.com/photos/2567025/pexels-photo-2567025.jpeg' },
  { id: 11, category: 'stall', title: 'Premium Exhibition Stand', image: 'https://images.pexels.com/photos/18877085/pexels-photo-18877085.jpeg' },
  { id: 12, category: 'activation', title: 'Consumer Engagement Event', image: 'https://images.unsplash.com/photo-1727931287903-b24dd8011a56?crop=entropy&cs=srgb&fm=jpg&q=85' },
];

const categories = [
  { id: 'all', label: 'All Work' },
  { id: 'expo', label: 'Expos' },
  { id: 'stall', label: 'Stall Building' },
  { id: 'event', label: 'Live Events' },
  { id: 'activation', label: 'Brand Activation' },
];

export default function GalleryPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);

  const filteredItems = activeCategory === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === activeCategory);

  return (
    <div className="page-transition" data-testid="gallery-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 bg-navy overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Our Portfolio
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">
                Showcasing <span className="text-gold">Excellence</span>
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed">
                Explore our portfolio of successful exhibitions, events, and brand activations that have made a lasting impact.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Filter Tabs */}
        <section className="py-8 bg-white border-b border-gray-100 sticky top-[72px] z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap gap-4 justify-center">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-colors ${
                    activeCategory === category.id
                      ? 'bg-navy text-white'
                      : 'bg-muted text-navy hover:bg-navy/10'
                  }`}
                  data-testid={`filter-${category.id}`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              layout
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 30 }}
                  transition={{ delay: index * 0.05 }}
                  className="gallery-item cursor-pointer"
                  onClick={() => setSelectedImage(item)}
                  data-testid={`gallery-item-${item.id}`}
                >
                  <div className="relative h-[300px] overflow-hidden group">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="overlay">
                      <div className="text-center">
                        <ZoomIn className="w-10 h-10 text-white mb-3 mx-auto" />
                        <h3 className="text-white font-heading text-xl">{item.title}</h3>
                        <span className="text-gold text-sm uppercase tracking-wider">
                          {categories.find(c => c.id === item.category)?.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Upload Notice */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-16 text-center bg-muted p-8"
            >
              <p className="text-muted-foreground">
                <span className="text-navy font-semibold">Note:</span> This gallery will be updated with your actual project images and videos.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Lightbox Modal */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
              data-testid="lightbox-modal"
            >
              <button 
                className="absolute top-6 right-6 text-white hover:text-gold transition-colors z-[101]"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(null);
                }}
                aria-label="Close"
                data-testid="lightbox-close"
              >
                <X className="w-8 h-8" />
              </button>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="max-w-5xl max-h-[80vh]"
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={selectedImage.image}
                  alt={selectedImage.title}
                  className="w-full h-full object-contain"
                />
                <div className="text-center mt-4">
                  <h3 className="text-white font-heading text-2xl">{selectedImage.title}</h3>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  );
}
